import React from 'react';
import Grid2 from '@mui/material/Grid2';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import TextField from '../../../components/TextField';
import Picklist from '../../../components/Picklist';
import FormSection from '../../../components/FormSection';
import { CaregiverFormData } from '../types';
import { FieldErrors } from '../validation';

interface Props {
  formData: CaregiverFormData;
  fieldErrors: FieldErrors;
  handleChange: (e: { target: { name: string; value: any } }) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultiSelectChange: (e: { target: { name: string; value: string[] } }) => void;
}

const EmergencyReferencesStep: React.FC<Props> = ({
  formData,
  fieldErrors,
  handleChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FormSection title={t('form.professionalReference', 'Professional Reference')} icon={<WorkIcon />}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="body2" color="textSecondary">
            {t('form.professionalReferenceDescription', 'Must be someone you currently work with or have worked with in the past')}
          </Typography>
        </Box>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              fullWidth
              required
              name="professionalReferenceName"
              label={t('form.professionalReferenceName', 'Name')}
              value={formData.professionalReferenceName}
              onChange={handleChange}
              error={!!fieldErrors.professionalReferenceName}
              helperText={fieldErrors.professionalReferenceName}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="professionalReferenceRelationship"
              label={t('form.professionalReferenceRelationship', 'Relationship')}
              value={formData.professionalReferenceRelationship}
              onChange={handleChange}
              error={!!fieldErrors.professionalReferenceRelationship}
              helperText={fieldErrors.professionalReferenceRelationship}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="professionalReferencePhone"
              label={t('form.professionalReferencePhone', 'Phone')}
              value={formData.professionalReferencePhone}
              onChange={handleChange}
              inputMask="999 999 9999"
              error={!!fieldErrors.professionalReferencePhone}
              helperText={fieldErrors.professionalReferencePhone}
            />
          </Grid2>
        </Grid2>
      </FormSection>

      <FormSection title={t('form.personalReference', 'Personal Reference')} icon={<PersonIcon />}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="body2" color="textSecondary">
            {t('form.personalReferenceDescription', 'Cannot be someone you are related to')}
          </Typography>
        </Box>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              fullWidth
              required
              name="personalReferenceName"
              label={t('form.personalReferenceName', 'Name')}
              value={formData.personalReferenceName}
              onChange={handleChange}
              error={!!fieldErrors.personalReferenceName}
              helperText={fieldErrors.personalReferenceName}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="personalReferenceRelationship"
              label={t('form.personalReferenceRelationship', 'Relationship')}
              value={formData.personalReferenceRelationship}
              onChange={handleChange}
              error={!!fieldErrors.personalReferenceRelationship}
              helperText={fieldErrors.personalReferenceRelationship}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="personalReferencePhone"
              label={t('form.personalReferencePhone', 'Phone')}
              value={formData.personalReferencePhone}
              onChange={handleChange}
              inputMask="999 999 9999"
              error={!!fieldErrors.personalReferencePhone}
              helperText={fieldErrors.personalReferencePhone}
            />
          </Grid2>
        </Grid2>
      </FormSection>

      <FormSection title={t('form.emergencyContact', 'Emergency Contact')} icon={<ContactEmergencyIcon />}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              fullWidth
              required
              name="emergencyContactName"
              label={t('form.emergencyContactName', 'Name')}
              value={formData.emergencyContactName}
              onChange={handleChange}
              error={!!fieldErrors.emergencyContactName}
              helperText={fieldErrors.emergencyContactName}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="emergencyContactRelationship"
              value={formData.emergencyContactRelationship}
              onChange={handleChange}
              label={t('form.emergencyContactRelationship', 'Relationship')}
              objectName="caregiver"
              required
              error={!!fieldErrors.emergencyContactRelationship}
              helperText={fieldErrors.emergencyContactRelationship}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="emergencyContactPhone"
              label={t('form.emergencyContactPhone', 'Phone')}
              value={formData.emergencyContactPhone}
              onChange={handleChange}
              inputMask="999 999 9999"
              error={!!fieldErrors.emergencyContactPhone}
              helperText={fieldErrors.emergencyContactPhone}
            />
          </Grid2>
        </Grid2>
      </FormSection>
    </>
  );
};

export default EmergencyReferencesStep;
